import React, { useState, useEffect } from 'react';
import { TbLayoutDashboard } from "react-icons/tb";
import { IoEyeOutline } from "react-icons/io5";
import { ImUsers } from "react-icons/im";
import { IoIosTrendingUp, IoIosTrendingDown } from "react-icons/io";
import { FaUserPlus } from "react-icons/fa";
import { IoScanOutline, IoPersonAddOutline, IoVideocamOutline, IoWalletOutline, IoCashOutline, IoCardOutline } from "react-icons/io5";
import '../css/dashboard.css';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const Dashboard = () => {
    const [stats, setStats] = useState([]);
    const [newUsersAnalytics, setNewUsersAnalytics] = useState({ labels: [], data: [] });
    const [loading, setLoading] = useState(true);
    const [creatorTeamStats, setCreatorTeamStats] = useState({
        creators: { value: 0, trend: 0 },
        teams: { value: 0, trend: 0 },
        total: { value: 0, trend: 0 }
    });
    const [creatorAnalytics, setCreatorAnalytics] = useState({
        total_views: { value: 0, trend: 0 },
        logo_scans: { value: 0, trend: 0 },
        followers: { value: 0, trend: 0 },
        subscribers: { value: 0, trend: 0 }
    });
    const [revenueStats, setRevenueStats] = useState({
        total_revenue: { value: 0, trend: 0 },
        monthly_revenue: { value: 0, trend: 0 },
        active_subscriptions: { value: 0, trend: 0 }
    });


    const getIcon = (iconName) => {
        const icons = {
            IoEyeOutline: <IoEyeOutline />,
            TbLayoutDashboard: <TbLayoutDashboard />,
            ImUsers: <ImUsers />,
            FaUserPlus: <FaUserPlus />
        };
        return icons[iconName] || <IoEyeOutline />;
    };

    const Api = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        const loadData = async (isSilent = false) => {
            const token = localStorage.getItem('access_token');
            if (!isSilent) setLoading(true);

            try {
                const response = await fetch(`${Api}admin/stats/dashboard`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                const rawData = await response.json();
                const data = rawData.data || rawData;

                const getVal = (obj) => (obj && typeof obj.value !== 'undefined') ? obj.value : (typeof obj === 'number' ? obj : 0);
                const getTrendVal = (obj) => (obj && typeof obj.trend !== 'undefined') ? obj.trend : 0;

                const displayStats = [
                    {
                        label: 'Total Users',
                        value: getVal(data.total_users),
                        icon: 'ImUsers',
                        trend: getTrendVal(data.total_users) >= 0 ? 'up' : 'down',
                        change: Math.abs(getTrendVal(data.total_users))
                    },
                    {
                        label: 'Total Posts',
                        value: getVal(data.total_posts),
                        icon: 'TbLayoutDashboard',
                        trend: getTrendVal(data.total_posts) >= 0 ? 'up' : 'down',
                        change: Math.abs(getTrendVal(data.total_posts))
                    },
                    {
                        label: 'Creators',
                        value: getVal(data.account_types?.creators),
                        icon: 'IoEyeOutline',
                        trend: getTrendVal(data.account_types?.creators) >= 0 ? 'up' : 'down',
                        change: Math.abs(getTrendVal(data.account_types?.creators))
                    },
                    {
                        label: 'Athletes',
                        value: getVal(data.account_types?.athletes),
                        icon: 'IoEyeOutline',
                        trend: getTrendVal(data.account_types?.athletes) >= 0 ? 'up' : 'down',
                        change: Math.abs(getTrendVal(data.account_types?.athletes))
                    },
                    {
                        label: 'Fans',
                        value: getVal(data.account_types?.fans),
                        icon: 'IoEyeOutline',
                        trend: getTrendVal(data.account_types?.fans) >= 0 ? 'up' : 'down',
                        change: Math.abs(getTrendVal(data.account_types?.fans))
                    },
                    {
                        label: 'Teams',
                        value: getVal(data.account_types?.teams),
                        icon: 'IoEyeOutline',
                        trend: getTrendVal(data.account_types?.teams) >= 0 ? 'up' : 'down',
                        change: Math.abs(getTrendVal(data.account_types?.teams))
                    }
                ];

                // Only update if data actually changed to prevent "jerking"
                setStats(prev => {
                    if (JSON.stringify(prev) === JSON.stringify(displayStats)) return prev;
                    return displayStats;
                });

                // Creator & Team Analytics from account_types
                const creatorsVal = getVal(data.account_types?.creators);
                const creatorsTrend = getTrendVal(data.account_types?.creators);
                const teamsVal = getVal(data.account_types?.teams);
                const teamsTrend = getTrendVal(data.account_types?.teams);
                const totalCT = creatorsVal + teamsVal;

                setCreatorTeamStats(prev => {
                    const updated = {
                        creators: { value: creatorsVal, trend: creatorsTrend },
                        teams: { value: teamsVal, trend: teamsTrend },
                        total: { value: totalCT, trend: creatorsTrend + teamsTrend }
                    };
                    if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
                    return updated;
                });

                const analyticsRaw =
                    data.new_users_analytics ||
                    data.new_user_analytics ||
                    data.user_analytics ||
                    data.monthly_registrations ||
                    data.registrations ||
                    data.visitor_analytics ||
                    null;

                if (analyticsRaw) {
                    setNewUsersAnalytics(prev => {
                        let updatedAnalytics = prev;
                        if (analyticsRaw.labels && analyticsRaw.data) {
                            updatedAnalytics = { labels: analyticsRaw.labels, data: analyticsRaw.data };
                        } else if (analyticsRaw.labels && analyticsRaw.this_week) {
                            updatedAnalytics = {
                                labels: analyticsRaw.labels,
                                data: analyticsRaw.this_week,
                                dataPrev: analyticsRaw.last_week || []
                            };
                        } else if (Array.isArray(analyticsRaw)) {
                            updatedAnalytics = {
                                labels: analyticsRaw.map(i => i.month || i.day || i.date || i.label || '?'),
                                data: analyticsRaw.map(i => i.count || i.value || i.users || i.new_users || 0)
                            };
                        } else if (typeof analyticsRaw === 'object') {
                            updatedAnalytics = {
                                labels: Object.keys(analyticsRaw),
                                data: Object.values(analyticsRaw).map(v => typeof v === 'number' ? v : 0)
                            };
                        }

                        if (JSON.stringify(prev) === JSON.stringify(updatedAnalytics)) return prev;
                        return updatedAnalytics;
                    });
                }

                if (!isSilent) setLoading(false);
            } catch (error) {
                console.error('Error loading dashboard data:', error);
                if (!isSilent) setLoading(false);
            }
        };

        loadData();

        // Fetch creator-team analytics
        const loadCreatorAnalytics = async (isSilent = false) => {
            const token = localStorage.getItem('access_token');
            try {
                const response = await fetch(`${Api}admin/stats/creator-team-analytics`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setCreatorAnalytics(prev => {
                        if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
                        return data;
                    });
                }
            } catch (error) {
                console.error('Error loading creator analytics:', error);
            }
        };

        // Fetch revenue stats
        const loadRevenueStats = async (isSilent = false) => {
            const token = localStorage.getItem('access_token');
            try {
                const response = await fetch(`${Api}admin/stats/revenue`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setRevenueStats(prev => {
                        if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
                        return data;
                    });
                }
            } catch (error) {
                console.error('Error loading revenue stats:', error);
            }
        };

        loadCreatorAnalytics();
        loadRevenueStats();

        // Real-time polling every 30 seconds
        const interval = setInterval(() => {
            loadData(true); // Silent update
            loadCreatorAnalytics(true);
            loadRevenueStats(true);
        }, 30000);

        return () => clearInterval(interval);
    }, [Api]);

    if (loading) {
        return (
            <div className="loader-container">
                <div className="theme-loader"></div>
            </div>
        );
    }

    // ── Chart config ──
    const hasChartData = newUsersAnalytics.labels && newUsersAnalytics.labels.length > 0;
    const chartLabels = hasChartData ? newUsersAnalytics.labels : ['No Data'];
    const chartValues = hasChartData ? newUsersAnalytics.data : [0];
    const totalNewUsers = chartValues.reduce((a, b) => a + b, 0);


    const mainDataset = {
        label: 'New Registrations',
        data: chartValues,
        backgroundColor: 'rgba(255, 255, 255, 0.15)', // Light white area fill
        borderColor: '#ffffff',                  // Solid white line for contrast
        borderWidth: 3,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: '#ff2d2d',                  // Red border on points to pop
        pointBorderWidth: 2,
        pointRadius: 5,                          // Show points clearly
        pointHoverRadius: 7,
        fill: true,
        tension: 0,                          // Straight lines for technical clarity
    };

    const lineData = { labels: chartLabels, datasets: [mainDataset] };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#ffffff',               // White tooltip for max contrast
                titleColor: '#000',
                bodyColor: '#000',
                titleFont: { size: 13, weight: 'bold' },
                bodyFont: { size: 12 },
                padding: 12,
                displayColors: false,
                borderColor: 'rgba(0, 0, 0, 0.1)',
                borderWidth: 1,
                callbacks: {
                    label: (ctx) => `Registrations: ${ctx.raw}`
                }
            }
        },
        scales: {
            x: {
                ticks: { color: 'rgba(255,255,255,0.9)', font: { size: 11, weight: '600' } },
                grid: { color: 'rgba(255,255,255,0.1)' },
                border: { color: 'rgba(255,255,255,0.2)' }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    color: 'rgba(255,255,255,0.9)',
                    font: { size: 11, weight: '600' },
                    stepSize: 1,
                    callback: (v) => Number.isInteger(v) ? v : null
                },
                grid: { color: 'rgba(255,255,255,0.1)' },
                border: { color: 'rgba(255,255,255,0.2)' }
            }
        }
    };

    return (
        <div className="dashboard-main-container">

            {/* Stats Grid */}
            <div className="dashboard-stats-grid">
                {stats.map((item, i) => (
                    <div key={i} className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon">{getIcon(item.icon)}</span>
                            <span className="stat-card-value">{item.value.toLocaleString()}</span>
                        </div>
                        <span className="stat-card-label">{item.label}</span>
                        <div className="stat-card-trend">
                            {item.trend === 'up'
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span
                                className="stat-trend-pct"
                                style={{ color: item.trend === 'up' ? '#4ade80' : '#f87171' }}
                            >
                                {item.change}%
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Chart */}
            <div className="dashboard-charts-grid">
                <div className="chart-card">

                    {/* Chart Header */}
                    <div className="chart-header">
                        <div className="chart-title-block">
                            <h3>
                                <FaUserPlus style={{ opacity: 0.9 }} />
                                New User Analytics
                            </h3>
                            <p>
                                Total:&nbsp;
                                <strong style={{ opacity: 1 }}>{totalNewUsers.toLocaleString()}</strong>
                                &nbsp;new registrations
                            </p>
                        </div>

                    </div>

                    {/* Chart Body */}
                    {!hasChartData ? (
                        <div className="chart-empty-state">
                            <FaUserPlus style={{ fontSize: '2.8rem', marginBottom: '0.5rem' }} />
                            <p style={{ fontSize: '1rem', fontWeight: '500', margin: 0 }}>
                                No analytics data available
                            </p>
                            <p style={{ fontSize: '0.8rem', margin: '4px 0 0', opacity: 0.7 }}>
                                API may not be returning analytics data yet
                            </p>
                        </div>
                    ) : (
                        <div className="chart-area">
                            <Line data={lineData} options={chartOptions} />
                        </div>
                    )}

                </div>
            </div>

            {/* Creator & Team Analytics */}
            <div className="creator-analytics-section">
                <div className="creator-analytics-header">
                    <h3>
                        <IoScanOutline />
                        Creator & Team Analytics
                    </h3>
                    <p>Total creators and teams overview</p>
                </div>
                <div className="creator-stats-grid">
                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoEyeOutline /></span>
                            <span className="stat-card-value">{(creatorAnalytics.total_views?.value || 0).toLocaleString()}</span>
                        </div>
                        <span className="stat-card-label">Total Views</span>
                        <div className="stat-card-trend">
                            {(creatorAnalytics.total_views?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (creatorAnalytics.total_views?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(creatorAnalytics.total_views?.trend || 0)}%
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoScanOutline /></span>
                            <span className="stat-card-value">{(creatorAnalytics.logo_scans?.value || 0).toLocaleString()}</span>
                        </div>
                        <span className="stat-card-label">Logo Scans</span>
                        <div className="stat-card-trend">
                            {(creatorAnalytics.logo_scans?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (creatorAnalytics.logo_scans?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(creatorAnalytics.logo_scans?.trend || 0)}%
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoPersonAddOutline /></span>
                            <span className="stat-card-value">{(creatorAnalytics.followers?.value || 0).toLocaleString()}</span>
                        </div>
                        <span className="stat-card-label">Followers</span>
                        <div className="stat-card-trend">
                            {(creatorAnalytics.followers?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (creatorAnalytics.followers?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(creatorAnalytics.followers?.trend || 0)}%
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoVideocamOutline /></span>
                            <span className="stat-card-value">{(creatorAnalytics.subscribers?.value || 0).toLocaleString()}</span>
                        </div>
                        <span className="stat-card-label">Subscribers</span>
                        <div className="stat-card-trend">
                            {(creatorAnalytics.subscribers?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (creatorAnalytics.subscribers?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(creatorAnalytics.subscribers?.trend || 0)}%
                            </span>
                        </div>
                    </div>
                    {/* <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoWalletOutline /></span>
                            <span className="stat-card-value">${(revenueStats.total_revenue?.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <span className="stat-card-label">Total Earnings</span>
                        <div className="stat-card-trend">
                            {(revenueStats.total_revenue?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (revenueStats.total_revenue?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(revenueStats.total_revenue?.trend || 0)}%
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoCashOutline /></span>
                            <span className="stat-card-value">${(revenueStats.monthly_revenue?.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <span className="stat-card-label">Monthly Earnings</span>
                        <div className="stat-card-trend">
                            {(revenueStats.monthly_revenue?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (revenueStats.monthly_revenue?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(revenueStats.monthly_revenue?.trend || 0)}%
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoCardOutline /></span>
                            <span className="stat-card-value">{(revenueStats.active_subscriptions?.value || 0).toLocaleString()}</span>
                        </div>
                        <span className="stat-card-label">Active Subscriptions</span>
                        <div className="stat-card-trend">
                            {(revenueStats.active_subscriptions?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (revenueStats.active_subscriptions?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(revenueStats.active_subscriptions?.trend || 0)}%
                            </span>
                        </div>
                    </div> */}
                </div>
            </div>



            {/* Subscription Revenue Totals */}
            <div className="subscription-section">
                <div className="subscription-header">
                    <h3>
                        <IoCashOutline />
                        Subscription Revenue Totals
                    </h3>
                    <p>App-wide subscription revenue overview</p>
                </div>
                <div className="subscription-stats-grid">
                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoCashOutline /></span>
                            <span className="stat-card-value">${(revenueStats.total_revenue?.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <span className="stat-card-label">Total Subscription Revenue</span>
                        <div className="stat-card-trend">
                            {(revenueStats.total_revenue?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (revenueStats.total_revenue?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(revenueStats.total_revenue?.trend || 0)}%
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoWalletOutline /></span>
                            <span className="stat-card-value">${(revenueStats.monthly_revenue?.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <span className="stat-card-label">Monthly Subscription Revenue</span>
                        <div className="stat-card-trend">
                            {(revenueStats.monthly_revenue?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (revenueStats.monthly_revenue?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(revenueStats.monthly_revenue?.trend || 0)}%
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-top">
                            <span className="stat-card-icon"><IoCardOutline /></span>
                            <span className="stat-card-value">{(revenueStats.active_subscriptions?.value || 0).toLocaleString()}</span>
                        </div>
                        <span className="stat-card-label">Active Subscriptions</span>
                        <div className="stat-card-trend">
                            {(revenueStats.active_subscriptions?.trend || 0) >= 0
                                ? <IoIosTrendingUp style={{ color: '#4ade80' }} />
                                : <IoIosTrendingDown style={{ color: '#f87171' }} />
                            }
                            <span className="stat-trend-pct" style={{ color: (revenueStats.active_subscriptions?.trend || 0) >= 0 ? '#4ade80' : '#f87171' }}>
                                {Math.abs(revenueStats.active_subscriptions?.trend || 0)}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Dashboard;
